import React, { useState, useRef, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStars } from "@fortawesome/pro-regular-svg-icons/faStars";
import styled from "@emotion/styled";
import { faChevronLeft } from "@fortawesome/pro-regular-svg-icons/faChevronLeft";
import { faChevronRight } from "@fortawesome/pro-regular-svg-icons/faChevronRight";
import cn from "classnames";
import dayjs from "dayjs";

const ScrollbarStyles = styled.div`
    ::-webkit-scrollbar {
        height: 0;
        /* height: 20px; */
    }

    /* 4px scroll thumb. 10px margin from right margin */
    /* ::-webkit-scrollbar-track {
        box-shadow: inset 0 0 15px 15px #000;
        border-top: solid 15px transparent;
    }

    ::-webkit-scrollbar-thumb {
        box-shadow: inset 0 0 15px 15px #333;
        border-top: solid 15px transparent;
    }

    ::-webkit-scrollbar-button {
        display: none;
    } */
`;

const Icon = () => (
    <div className="flex py-10 justify-center items-center w-full">
        <FontAwesomeIcon icon={faStars} size="6x" />
    </div>
);

const ReviewsComponent = ({
    state: {
        reviews = [],
        heading = "What they say",
        border,
        selectedCardIndex = null,
    },
    isEditMode,
    onChange,
    focused,
}) => {
    const [isAtStart, setIsAtStart] = useState(true);
    const [isAtEnd, setIsAtEnd] = useState(false);

    const reviewsRef = useRef(null);

    const scroll = (direction) => {
        if (reviewsRef.current) {
            const { current } = reviewsRef;
            const gap = 28;
            let scrollAmount = 330 + gap;
            const review = document.querySelector(
                '[data-testid="reviews__item-0"]'
            );

            if (review) {
                scrollAmount = review.clientWidth + gap;
            }

            if (direction === "left") {
                current.scrollLeft -= scrollAmount;
            } else {
                current.scrollLeft += scrollAmount;
            }
        }
    };

    const checkScrollPosition = () => {
        if (reviewsRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = reviewsRef.current;

            setIsAtStart(scrollLeft <= 0);
            setIsAtEnd(scrollLeft + clientWidth >= scrollWidth);
        }
    };

    useEffect(() => {
        checkScrollPosition();
        const scrollDiv = reviewsRef.current;

        if (scrollDiv) {
            scrollDiv.addEventListener("scroll", checkScrollPosition);
            window.addEventListener("resize", checkScrollPosition);
            return () => {
                scrollDiv.removeEventListener("scroll", checkScrollPosition);
                window.removeEventListener("resize", checkScrollPosition);
            };
        }
    }, []);

    return (
        <>
            <div className="flex flex-col items-center justify-between md:flex-row mb-[30px]">
                <h2
                    data-testid="product-relations__title"
                    className={
                        "text-center !text-[30px] !leading-[34px] text-brand-black !lg:text-[40px] !lg:leading-[46px] !mb-0"
                    }
                >
                    {heading}
                </h2>

                <div className="hidden gap-[10px] md:flex items-center">
                    <button
                        className={cn(
                            `h-[50px] w-[50px] rounded-full bg-brand-light-grey text-[20px] leading-[26px] text-brand-black hover:bg-brand-light-grey active:bg-brand-grey`,
                            {
                                "cursor-not-allowed opacity-25": isAtStart,
                            }
                        )}
                        onClick={() => scroll("left")}
                        disabled={isAtStart}
                    >
                        <FontAwesomeIcon icon={faChevronLeft} />
                    </button>
                    <button
                        className={cn(
                            `h-[50px] w-[50px] rounded-full bg-brand-light-grey text-[20px] leading-[26px] text-brand-black hover:bg-brand-light-grey active:bg-brand-grey`,
                            {
                                "cursor-not-allowed opacity-25": isAtEnd,
                            }
                        )}
                        onClick={() => scroll("right")}
                        disabled={isAtEnd}
                    >
                        <FontAwesomeIcon icon={faChevronRight} />
                    </button>
                </div>
            </div>

            {reviews?.length > 0 ? (
                <ScrollbarStyles
                    ref={reviewsRef}
                    className="flex scroll-smooth  flex-row content__reviews overflow-auto gap-[30px]"
                >
                    {reviews.map((review, idx) => {
                        return (
                            <div
                                key={`reviews_${idx}`}
                                data-testid={`reviews__item-${idx}`}
                                style={{
                                    boxShadow:
                                        selectedCardIndex === idx &&
                                        focused &&
                                        "inset 0 0 0 3px red",
                                }}
                                className={`flex flex-col w-[270px] min-w-[270px] max-w-[270px] lg:flex-[420px] lg:min-w-[420px] lg:max-w-[420px] gap-y-[15px] p-[15px] lg:p-[30px] rounded-[10px] bg-brand-white`}
                                onClick={(e) => {
                                    if (isEditMode) {
                                        e.preventDefault();
                                        onChange({
                                            selectedCardIndex: idx,
                                        });
                                    }
                                }}
                            >
                                {review.rating && (
                                    <div className="flex flex-row items-center gap-x-[5px]">
                                        {Array.from({
                                            length: review.rating,
                                        }).map((_, starIdx) => (
                                            <svg
                                                key={`review-${idx}-${starIdx}`}
                                                width="30"
                                                height="28"
                                                viewBox="0 0 30 28"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                                className="text-[#FCBF33]"
                                            >
                                                <path
                                                    d="M15 0L18.5267 10.1459L29.2658 10.3647L20.7063 16.8541L23.8168 27.1353L15 21L6.18322 27.1353L9.29366 16.8541L0.734152 10.3647L11.4733 10.1459L15 0Z"
                                                    fill="#FCBF33"
                                                />
                                            </svg>
                                        ))}
                                    </div>
                                )}
                                <p
                                    className={`flex font-bold text-[20px] leading-[26px]`}
                                >
                                    {review.content}
                                </p>

                                <div className="flex flex-row flex-wrap items-center gap-x-[15px]">
                                    <p className="text-brand-black !mb-0">
                                        {review.customerName || ""}
                                    </p>

                                    {review.date && (
                                        <p className="text-brand-black !mb-0">
                                            {dayjs(review.date).format(
                                                "DD-MM-YYYY"
                                            )}
                                        </p>
                                    )}

                                    {review.isVerifiedBuyer && (
                                        <p className="text-brand-black !mb-0">
                                            Verified buyer
                                        </p>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </ScrollbarStyles>
            ) : (
                <Icon />
            )}

            <div className="mt-[15px] flex items-center justify-center gap-[10px] md:hidden">
                <button
                    className={cn(
                        `h-[50px] w-[50px] rounded-full bg-brand-light-grey text-[20px] leading-[26px] text-brand-black hover:bg-brand-light-grey active:bg-brand-grey`,
                        {
                            "cursor-not-allowed opacity-25": isAtStart,
                        }
                    )}
                    onClick={() => scroll("left")}
                    disabled={isAtStart}
                >
                    <FontAwesomeIcon icon={faChevronLeft} />
                </button>
                <button
                    className={cn(
                        `h-[50px] w-[50px] rounded-full bg-brand-light-grey text-[20px] leading-[26px] text-brand-black hover:bg-brand-light-grey active:bg-brand-grey`,
                        {
                            "cursor-not-allowed opacity-25": isAtEnd,
                        }
                    )}
                    onClick={() => scroll("right")}
                    disabled={isAtEnd}
                >
                    <FontAwesomeIcon icon={faChevronRight} />
                </button>
            </div>
        </>
    );
};

export default ReviewsComponent;
